// Per-match broadcaster display for Portugal, used to show "where to watch"
// next to each fixture.
//
// What's publicly confirmed (see the "Onde assistir" article):
//   - Sport TV (paid): ALL 104 games. The exact channel (Sport TV1 / Sport
//     TV5) is mapped per game where published; otherwise a generic "Sport TV".
//   - Free-to-air (RTP1 / SIC / TVI): the specific channel per game, from the
//     published group-stage grid (see GROUP_CHANNELS in fixtures.ts).
//   - LiveModeTV (free, YouTube): its package explicitly includes every
//     Portugal game + both semi-finals + the final. The full per-day list
//     isn't reliably mapped to fixtures, so we only flag the confirmed ones.

import type { Channel, Match } from "./fixtures";

// The outlets say to "search YouTube for LiveMode TV" — there's no published
// canonical channel URL, so we link to that search (always resolves).
export const LIVEMODE_YT_URL =
  "https://www.youtube.com/results?search_query=LiveMode+TV";

export type ChannelMeta = {
  /** Display label — a proper noun, identical across languages. */
  label: string;
  /** Paid (subscription) vs free-to-air, drives chip styling. */
  kind: "paid" | "free";
  /** External link (LiveModeTV only). */
  href?: string;
};

export const CHANNEL_META: Record<Channel, ChannelMeta> = {
  sporttv: { label: "Sport TV", kind: "paid" },
  sporttv1: { label: "Sport TV1", kind: "paid" },
  sporttv5: { label: "Sport TV5", kind: "paid" },
  rtp1: { label: "RTP1", kind: "free" },
  sic: { label: "SIC", kind: "free" },
  tvi: { label: "TVI", kind: "free" },
  livemodetv: { label: "LiveModeTV", kind: "free", href: LIVEMODE_YT_URL },
};

export function involvesPortugal(m: Match): boolean {
  return m.home === "pt" || m.away === "pt";
}

/** LiveModeTV (free on YouTube) confirmed to carry this game. */
export function liveModeCarries(m: Match): boolean {
  return involvesPortugal(m) || m.stage === "sf" || m.stage === "final";
}

/** Ordered list of channels to show for a match: its mapped broadcasters plus
 *  LiveModeTV where confirmed (deduped). */
export function channelsFor(m: Match): Channel[] {
  const list = [...m.channels];
  if (liveModeCarries(m) && !list.includes("livemodetv")) {
    list.push("livemodetv");
  }
  return list;
}
